import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import BellIcon from "../ui/icons/BellIcon";
import SiteMapIcon from "../ui/icons/SiteMapIcon";
import UserBox from "../user/user-box";
import useSideBarStore from "@/stores/side-bar";
import SideBarIcon from "../ui/icons/SideBarIcon";
import { Text } from "../ui/text";
import HeaderArrowIcon from "../ui/icons/HeaderArrowIcon";
import useAccountInfoQuery from "@/hooks/services/queries/use-account-info-query";
import ManualPopup from "../shared/manual-popup";
import LanguagePopup from "../shared/language-popup";
import { useTranslation } from "react-i18next";
import { AI_HOME_PATHNAME } from "@/constants/pathname";

const MainHeader = () => {
  const { t } = useTranslation();

  const MENUS = [
    {
      name: t("layout.home"),
      href: "/home/dashboard",
      parent: "dashboard",
    },
    {
      name: t("layout.job"),
      href: "/job/job-status",
      children: [
        {
          name: t("layout.status"),
          href: "/job/job-status",
          parent: "job",
        },
        {
          name: t("layout.planning"),
          href: "/job/job-planning",
          parent: "job",
        },
      ],
      parent: "job",
    },
    {
      name: t("layout.searched"),
      href: "/searched",
      parent: "searched",
    },
    {
      name: t("layout.statistics"),
      href: "/statistics",
      parent: "statistics",
    },
    {
      name: t("layout.bioinformatics"),
      href: "/bioinformatics",
      parent: "bioinformatics",
    },
    {
      name: "AI Agent",
      href: AI_HOME_PATHNAME,
      parent: "ai",
    },

    {
      name: t("layout.myPage"),
      href: "/home/mypage",
      parent: "mypage",
    },
  ];
  const location = useLocation();
  const { pathname, search } = location;

  const navigate = useNavigate();

  const queryParams = new URLSearchParams(search);
  const queryType = queryParams.get("type");

  const [isOpen, setIsOpen] = useState(false);

  const { data: accountInfo } = useAccountInfoQuery();

  const handleButtonClick = () => {
    setIsOpen((prev) => !prev);
  };

  const { toggleSideBar, isOpen: isSideBarOpen } = useSideBarStore();

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const defaultLogo = useMemo(() => {
    if (import.meta.env.VITE_APP_SERVER_NAME === "sbh") {
      return "/images/default_logo_sbh.png";
    }

    if (import.meta.env.VITE_APP_SERVER_NAME === "kist") {
      return "/images/default_logo_kist.png";
    }

    return "/images/default_logo_saas.png";
  }, []);

  const pageNames = useMemo(() => {
    if (pathname === "/home/dashboard") {
      return [t("layout.dashboard")];
    }
    if (pathname.includes("/home/announcement")) {
      return [t("layout.announcement")];
    }
    if (pathname === "/home/mypage") {
      return [t("layout.myPage")];
    }
    if (pathname === "/job/job-status") {
      return [t("layout.job"), t("layout.status")];
    }
    if (pathname === "/job/job-planning") {
      return [t("layout.job"), t("layout.planning")];
    }
    if (pathname === "/searched") {
      return [t("layout.searched")];
    }
    if (pathname === "/statistics") {
      return [t("layout.statistics")];
    }
    if (pathname === "/bioinformatics") {
      return [t("layout.bioinformatics")];
    }
    if (pathname.includes("/ai")) {
      return ["AI Agent"];
    }
  }, [pathname, t]);

  return (
    <>
      <header className="h-[70px] border-b py-[15px] pl-[16px] pr-[16px] md:pr-[48px] flex justify-between w-full">
        <div className={`flex min-w-0 ${isSideBarOpen ? "gap-[36px]" : "gap-[56px]"}`}>
          <div
            className={`font-bold text-xl text-green-700 flex justify-around items-center gap-[38px] shrink-0 ${
              isSideBarOpen ? "pl-2" : ""
            }`}
          >
            {isSideBarOpen &&
              (accountInfo?.logo ? (
                <img
                  onClick={() => navigate("/")}
                  className="cursor-pointer"
                  src={`https://dd8er8j5vek29.cloudfront.net/${accountInfo?.logo}`}
                  alt="custom logo"
                  width={124}
                  height={43}
                />
              ) : (
                <img
                  className="cursor-pointer"
                  onClick={() => navigate("/")}
                  src={defaultLogo}
                  alt="custom logo"
                  width={124}
                  height={43}
                />
              ))}
            <button
              onClick={toggleSideBar}
              data-testid="toggle-sidebar-btn"
              className={`rounded-[4px] w-[40px] h-[40px] flex justify-center items-center ${
                isSideBarOpen ? "bg-[#F2F2F2]" : ""
              }`}
            >
              <SideBarIcon color={isSideBarOpen ? "#0AC4B2" : "#333333"} />
            </button>
          </div>
          <div className="flex items-center min-w-0 overflow-hidden">
            <Text variant={"headline-sm"} className="text-grey-20 truncate">
              {pageNames?.[0]}
            </Text>
            {pageNames?.[1] && <HeaderArrowIcon />}
            {pageNames?.[1] && (
              <Text variant={"headline-sm"} className="text-grey-20 truncate">
                {pageNames[1]}
              </Text>
            )}
            {queryType && <HeaderArrowIcon />}
            {queryType && (
              <Text variant={"headline-sm"} className="text-grey-20 truncate">
                {queryType === "research" && t("layout.reSearch")}
                {queryType === "reanalysis" && t("layout.reAnalysis")}
              </Text>
            )}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex gap-[16px]">
            <button
              data-testid="announcement-btn"
              onClick={() => navigate("/home/announcement")}
              className="w-[40px] h-[40px] flex justify-center items-center"
            >
              <BellIcon />
            </button>
            <UserBox />
          </div>
          <div className="hidden md:block w-[2px] h-[40px] bg-[#E5E5E5] mx-[24px]"></div>
          <div className="hidden md:flex gap-[16px]">
            <button
              data-testid="toggle-sidemap-btn"
              onClick={handleButtonClick}
              className={`rounded-[4px] w-[40px] h-[40px] flex justify-center items-center ${
                isOpen ? "bg-grey-95" : ""
              }`}
            >
              <SiteMapIcon color={isOpen ? "#0AC4B2" : "#4D4D4D"} />
            </button>
            <ManualPopup />
            <div className="w-[2px] h-[40px] bg-[#E5E5E5] mx-[8px]"></div>
            <LanguagePopup />
          </div>
        </div>
      </header>
      {isOpen && (
        <div
          className="bg-black h-full bg-opacity-20 absolute w-full z-20"
          data-testid="menu-overlay"
        >
          <div className=" p-3 bg-white w-full z-10">
            <div className="flex flex-wrap gap-[40px] md:gap-[90px] pl-[16px] md:pl-[232px] pr-[16px] md:pr-[48px] pb-[40px] md:pb-[80px] pt-[24px] md:pt-[40px]">
              {MENUS.map((menu, index) => (
                <div key={index} className="flex flex-col gap-[19px]">
                  <Text variant={"title-xl"}>
                    <Link to={menu.href}>{menu.name}</Link>
                  </Text>
                  <div className="flex flex-col gap-[10px]">
                    {menu?.children?.map((child, idx) => (
                      <Text variant={"title-md"} key={idx}>
                        <Link to={child.href}>{child.name}</Link>
                      </Text>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MainHeader;
